<template>
  <div
    class="size-full overflow-y-auto overflow-x-hidden sm:px-6 lg:px-8"
  >
    <div
      class="flex min-h-full flex-col justify-center py-12"
    >
      <div class="mx-auto max-w-sm">
        <BaseCard>
          <div class="space-y-4">
            <h2 class="text-xl font-semibold"
              >Forgot Password</h2
            >
            <p class="text-gray-600">
              Enter your account details and we'll send you
              a link to reset your password.
            </p>

            <form
              novalidate
              class="space-y-6"
              @submit="onFormSubmit"
            >
              <FormInputText
                v-if="userType === 'merchant-user'"
                name="merchantId"
                label="Merchant Identifier"
                autocomplete="off"
              />
              <FormInputText
                name="email"
                type="email"
                autocomplete="email"
                label="Email Address"
              />

              <FormSubmitButton
                :is-submitting="isSubmitting"
                class="w-full justify-center text-lg"
              >
                Send Reset Link
              </FormSubmitButton>
            </form>
          </div>
        </BaseCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed } from "vue"
import { z } from "zod"
import {
  emailAddressSchema,
  requiredTrimmedString,
  trimmedString,
} from "@/validation/sharedStringFieldSchemas"

const props = defineProps<{
  userType: "merchant-user" | "merchant-group-admin"
}>()

const router = useRouter()

const formSchema = computed(() =>
  z.object({
    email: emailAddressSchema("Email is required"),
    merchantId:
      props.userType === "merchant-user"
        ? requiredTrimmedString(
            "Merchant Identifier is required"
          )
        : trimmedString().optional(),
  })
)

const { handleSubmit, isSubmitting } = useForm({
  validationSchema: computed(() =>
    toTypedSchema(formSchema.value)
  ),
})

const onFormSubmit = handleSubmit(async (values) => {
  try {
    if (
      props.userType === "merchant-user" &&
      values.merchantId
    ) {
      // Merchant endpoint
      await requestPasswordReset({
        email: values.email,
        merchantId: values.merchantId,
      })
    } else {
      // Merchant group endpoint
      await requestMerchantGroupAdminPasswordReset({
        email: values.email,
      })
    }

    toast.success("Password reset link sent to your email")
    router.push({
      name:
        props.userType === "merchant-user"
          ? "login"
          : "loginMerchantGroup",
    })
  } catch {
    toast.error("Failed to send password reset link")
  }
})
</script>
